export type SearchExpansionIntent = {
  cleanQuery?: string;
  tokens?: string[];
  phraseTokens?: string[];
  itemHints?: string[];
  categoryHints?: string[];
  locationHints?: string[];
  serviceHints?: string[];
  mustHave?: string[];
  nearLocation?: string | null;
};

export type DirectorySearchExpansion = {
  originalQuery: string;
  normalizedQuery: string;
  queryTokens: string[];
  candidateTerms: string[];
  boostTerms: string[];
  phraseAliases: string[];
  locationTerms: string[];
  locationAliases: string[];
  anchorTerms: string[];
  domainAnchors: string[];
  prefixTerms: string[];
  expandedTerms: string[];
};

const MAX_TERMS = 80;
const MAX_CANDIDATE_TERMS = 36;
const MAX_PREFIX_TERMS = 40;

const SEARCH_STOP_WORDS = new Set([
  "a",
  "an",
  "and",
  "around",
  "at",
  "by",
  "for",
  "from",
  "i",
  "im",
  "in",
  "looking",
  "near",
  "nearby",
  "need",
  "now",
  "of",
  "on",
  "open",
  "or",
  "still",
  "the",
  "today",
  "tonight",
  "to",
  "want",
  "with",
]);

const LOCATION_PREPOSITIONS = new Set([
  "around",
  "at",
  "in",
  "near",
  "nearby",
  "within",
]);

const LOCATION_TRAILING_STOP_WORDS = new Set([
  "currently",
  "me",
  "my",
  "now",
  "open",
  "right",
  "still",
  "the",
  "today",
  "tonight",
]);

const GENERIC_DIRECTORY_TERMS = new Set([
  "available",
  "business",
  "businesses",
  "company",
  "for sale",
  "provider",
  "providers",
  "sale",
  "sales",
  "sell",
  "selling",
  "service",
  "services",
  "shop",
  "shops",
  "store",
  "stores",
]);

const BOOST_ONLY_TERMS = new Set([
  "coordinator",
  "coordination",
  "management",
  "organiser",
  "organizer",
  "setup",
  "set up",
]);

const EVENT_ANCHORS = new Set([
  "birthday",
  "corporate",
  "decor",
  "decoration",
  "decorations",
  "event",
  "events",
  "party",
  "wedding",
]);

const LAND_ANCHORS = new Set([
  "diligence",
  "land",
  "plot",
  "plots",
  "properties",
  "property",
  "verification",
  "verify",
]);

const PRODUCT_ANCHORS = new Set([
  "bakery",
  "cake",
  "cakes",
  "laptop",
  "phone",
  "repair",
]);

const SAFE_CANDIDATE_ALIASES: Record<string, string[]> = {
  bakery: ["bakery", "bakeries", "cake", "cakes"],
  bakeries: ["bakery", "bakeries", "cake", "cakes"],
  cake: ["cake", "cakes", "bakery", "bakeries"],
  cakes: ["cake", "cakes", "bakery", "bakeries"],
  land: ["land", "plot", "plots", "property", "properties", "real estate"],
  plot: ["plot", "plots", "land", "property", "properties", "real estate"],
  plots: ["plot", "plots", "land", "property", "properties", "real estate"],
  property: ["property", "properties", "land", "plot", "plots", "real estate"],
  properties: ["property", "properties", "land", "plot", "plots", "real estate"],
  verification: ["verification", "verify", "plot verification"],
  verify: ["verify", "verification", "plot verification"],
  "black forest cake": ["black forest cake", "black forest", "forest cake", "cake"],
  "cake shop": ["cake shop", "cake", "cakes", "bakery"],
  "due diligence": [
    "due diligence",
    "land due diligence",
    "verification",
    "document check",
    "document checks",
    "land search",
    "title search",
    "property search",
  ],
  "land search": [
    "land search",
    "property search",
    "title search",
    "due diligence",
    "verification",
  ],
  "legal services": [
    "legal services",
    "due diligence",
    "verification",
    "document check",
    "land search",
  ],
  "plot verification": [
    "plot verification",
    "verification",
    "plot",
    "plots",
    "land search",
    "property search",
  ],
  "property search": [
    "property search",
    "land search",
    "title search",
    "due diligence",
    "verification",
  ],
  "title search": [
    "title search",
    "land search",
    "property search",
    "due diligence",
    "verification",
  ],
};

const LOCATION_ALIASES: Record<string, string[]> = {
  kiambu: ["kikuyu"],
  kikuyu: ["kiambu"],
};

const EVENT_PLANNING_PHRASES = [
  "event planner",
  "event planning",
  "event planning services",
  "event management",
  "event organizer",
  "event organiser",
  "event coordinator",
  "event coordination",
  "party planner",
  "party planning",
  "wedding planner",
  "wedding planning",
  "birthday party planning",
  "corporate events",
  "event decor",
  "event decoration",
  "event decorations",
  "event setup",
  "event set up",
  "event decor setup",
];

export function normalizeDirectorySearchText(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9\s-]/g, " ")
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function tokenizeDirectorySearchText(value: string): string[] {
  const normalized = normalizeDirectorySearchText(value);
  if (!normalized) return [];

  return normalized
    .split(" ")
    .map((token) => token.trim())
    .filter((token) => token.length > 1 && !SEARCH_STOP_WORDS.has(token));
}

export function uniqueSearchStrings(
  values: (string | null | undefined)[],
  maxCount = Number.POSITIVE_INFINITY
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];

  for (const value of values) {
    const clean = normalizeDirectorySearchText(value ?? "");
    if (!clean || seen.has(clean)) continue;

    seen.add(clean);
    out.push(clean);

    if (out.length >= maxCount) break;
  }

  return out;
}

function addSimpleAliases(term: string): string[] {
  const clean = normalizeDirectorySearchText(term);
  if (!clean || clean.includes(" ")) return clean ? [clean] : [];

  const out = new Set<string>([clean]);

  if (clean.endsWith("ies") && clean.length > 4) {
    out.add(`${clean.slice(0, -3)}y`);
  } else if (clean.endsWith("s") && !clean.endsWith("ss") && clean.length > 4) {
    out.add(clean.slice(0, -1));
  } else if (!clean.endsWith("s") && clean.length >= 4) {
    out.add(`${clean}s`);
  }

  return Array.from(out);
}

export function getSearchCandidateAliases(value: string): string[] {
  const clean = normalizeDirectorySearchText(value);
  if (!clean) return [];

  return uniqueSearchStrings([
    clean,
    ...addSimpleAliases(clean),
    ...(SAFE_CANDIDATE_ALIASES[clean] ?? []),
  ]);
}

function hasAnyToken(tokens: string[], values: Iterable<string>): boolean {
  for (const value of values) {
    if (tokens.includes(value)) return true;
  }

  return false;
}

function buildQueryPhrases(tokens: string[]): string[] {
  const out: string[] = [];

  for (let start = 0; start < tokens.length; start += 1) {
    const maxEnd = Math.min(tokens.length, start + 4);

    for (let end = start + 2; end <= maxEnd; end += 1) {
      out.push(tokens.slice(start, end).join(" "));
    }
  }

  return uniqueSearchStrings(out, 30);
}

function extractQueryLocationTerms(originalQuery: string): string[] {
  const words = normalizeDirectorySearchText(originalQuery).split(" ").filter(Boolean);
  const out: string[] = [];

  for (let index = words.length - 2; index >= 0; index -= 1) {
    if (!LOCATION_PREPOSITIONS.has(words[index])) continue;

    const tail = words
      .slice(index + 1)
      .filter((word) => !LOCATION_TRAILING_STOP_WORDS.has(word))
      .slice(0, 4);

    if (tail.length > 0) {
      out.push(tail.join(" "));
      out.push(...tail);
    }

    break;
  }

  return uniqueSearchStrings(out, 10);
}

function getIntentLocationTerms(
  intent: SearchExpansionIntent | null | undefined
): string[] {
  if (!intent) return [];

  return uniqueSearchStrings([
    ...(intent.locationHints ?? []),
    intent.nearLocation ?? "",
  ]);
}

function termTouchesLocation(term: string, locationTerms: string[]): boolean {
  const clean = normalizeDirectorySearchText(term);
  if (!clean) return false;

  const termTokens = tokenizeDirectorySearchText(clean);

  return locationTerms.some((locationTerm) => {
    const location = normalizeDirectorySearchText(locationTerm);
    if (!location) return false;
    if (clean === location) return true;

    const locationTokens = tokenizeDirectorySearchText(location);
    return termTokens.some((token) => locationTokens.includes(token));
  });
}

function isGenericTerm(term: string): boolean {
  return GENERIC_DIRECTORY_TERMS.has(normalizeDirectorySearchText(term));
}

function isBoostOnlyTerm(term: string): boolean {
  return BOOST_ONLY_TERMS.has(normalizeDirectorySearchText(term));
}

function buildDomainAnchors(tokens: string[]): string[] {
  const anchors: string[] = [];

  for (const token of tokens) {
    if (
      EVENT_ANCHORS.has(token) ||
      LAND_ANCHORS.has(token) ||
      PRODUCT_ANCHORS.has(token)
    ) {
      anchors.push(token);
    }
  }

  return uniqueSearchStrings(anchors);
}

function hasEventPlanningIntent(tokens: string[], phrases: string[]): boolean {
  const phraseText = phrases.join(" ");
  const hasEventAnchor =
    hasAnyToken(tokens, EVENT_ANCHORS) ||
    /\b(event|party|wedding|birthday|corporate)\b/.test(phraseText);
  const hasPlannerIntent =
    hasAnyToken(tokens, [
      "coordinator",
      "coordination",
      "decor",
      "decoration",
      "decorations",
      "management",
      "organiser",
      "organizer",
      "planner",
      "planning",
      "setup",
    ]) || /\b(planner|planning|organizer|organiser|management|decor|setup)\b/.test(phraseText);

  return hasEventAnchor && hasPlannerIntent;
}

function buildPhraseAliases(tokens: string[], phrases: string[]): string[] {
  const aliases: string[] = [];

  if (hasEventPlanningIntent(tokens, phrases)) {
    aliases.push(...EVENT_PLANNING_PHRASES);
  }

  if (
    hasAnyToken(tokens, LAND_ANCHORS) &&
    (hasAnyToken(tokens, ["diligence", "search", "verification", "verify"]) ||
      phrases.some((phrase) => phrase.includes("due diligence")))
  ) {
    aliases.push(
      "land due diligence",
      "due diligence",
      "plot verification",
      "land search",
      "property search",
      "title search"
    );
  }

  if (hasAnyToken(tokens, ["plot", "plots"]) && hasAnyToken(tokens, ["verification", "verify"])) {
    aliases.push("plot verification", "property search", "land search");
  }

  if (phrases.includes("black forest cake")) {
    aliases.push("black forest cake", "black forest", "forest cake");
  }

  if (phrases.includes("cake shop") || hasAnyToken(tokens, ["bakery"])) {
    aliases.push("cake shop", "bakery", "cake", "cakes");
  }

  return uniqueSearchStrings(aliases, MAX_TERMS);
}

function buildLocationAliases(locationTerms: string[]): string[] {
  return uniqueSearchStrings(
    locationTerms.flatMap((term) => LOCATION_ALIASES[term] ?? []),
    12
  );
}

function buildBoostTerms(
  seedTerms: string[],
  phraseAliases: string[],
  domainAnchors: string[]
): string[] {
  const tokenBoosts = seedTerms
    .flatMap((term) => tokenizeDirectorySearchText(term))
    .filter((token) => isBoostOnlyTerm(token) || isGenericTerm(token));
  const phraseBoosts = phraseAliases.flatMap((term) =>
    tokenizeDirectorySearchText(term).filter((token) => isBoostOnlyTerm(token))
  );

  return uniqueSearchStrings(
    [...tokenBoosts, ...phraseBoosts, ...domainAnchors],
    MAX_TERMS
  );
}

function buildCandidateTerms(seedTerms: string[], phraseAliases: string[]): string[] {
  const rawCandidates = seedTerms.filter(
    (term) => !isGenericTerm(term) && !isBoostOnlyTerm(term)
  );
  const aliasCandidates = rawCandidates.flatMap((term) =>
    getSearchCandidateAliases(term)
  );

  return uniqueSearchStrings(
    [...rawCandidates, ...phraseAliases, ...aliasCandidates],
    MAX_CANDIDATE_TERMS
  );
}

function getIntentCoreTerms(intent: SearchExpansionIntent | null | undefined): string[] {
  if (!intent) return [];

  return [
    intent.cleanQuery ?? "",
    ...(intent.mustHave ?? []),
    ...(intent.itemHints ?? []),
    ...(intent.serviceHints ?? []),
    ...(intent.categoryHints ?? []),
    ...(intent.phraseTokens ?? []),
    ...(intent.tokens ?? []),
  ];
}

export function buildDirectorySearchExpansion(
  originalQuery: string,
  intent?: SearchExpansionIntent | null
): DirectorySearchExpansion {
  const normalizedQuery = normalizeDirectorySearchText(originalQuery);
  const queryTokens = tokenizeDirectorySearchText(normalizedQuery);
  const queryPhrases = buildQueryPhrases(queryTokens);
  const locationTerms = uniqueSearchStrings([
    ...extractQueryLocationTerms(originalQuery),
    ...getIntentLocationTerms(intent),
  ]);
  const locationAliases = buildLocationAliases(locationTerms);
  const intentCoreTerms = getIntentCoreTerms(intent);

  const seedTerms = uniqueSearchStrings(
    [
      ...queryTokens,
      ...queryPhrases,
      ...intentCoreTerms,
    ].filter((term) => !termTouchesLocation(term, locationTerms)),
    MAX_TERMS
  );

  const domainAnchors = buildDomainAnchors([
    ...queryTokens,
    ...seedTerms.flatMap((term) => tokenizeDirectorySearchText(term)),
  ]);
  const phraseAliases = buildPhraseAliases(queryTokens, [
    ...queryPhrases,
    ...intentCoreTerms.map((term) => normalizeDirectorySearchText(term)),
  ]);
  const anchorTerms = uniqueSearchStrings([...domainAnchors]);
  const candidateTerms = buildCandidateTerms(seedTerms, phraseAliases);
  const boostTerms = buildBoostTerms(seedTerms, phraseAliases, domainAnchors);
  const prefixTerms = uniqueSearchStrings(
    [
      ...candidateTerms,
      ...phraseAliases,
      ...anchorTerms,
      ...locationTerms,
    ],
    MAX_PREFIX_TERMS
  );

  return {
    originalQuery,
    normalizedQuery,
    queryTokens,
    candidateTerms,
    boostTerms,
    phraseAliases,
    locationTerms,
    locationAliases,
    anchorTerms,
    domainAnchors,
    prefixTerms,
    expandedTerms: uniqueSearchStrings([
      ...candidateTerms,
      ...phraseAliases,
      ...boostTerms,
      ...locationTerms,
      ...locationAliases,
    ]),
  };
}
